import { History } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { approveApplicationRequest, rejectApplicationRequest } from "./referralApplicationApi";

const MODULE_LABELS = {
  admission: "Admission",
  visa: "Visa",
  insurance: "Insurance",
  "skill-assessment": "Skill Assessment",
  "rpl-course": "RPL",
  other: "Other",
};

/**
 * Shared approve/reject dialogs for referral application requests, used by the per-client section
 * and the central review queue. Approving creates the real module entry with the partner's details
 * filled in; rejecting returns the request with a reason the partner can act on.
 */
const ApplicationReviewDialogs = ({ approving, rejecting, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNote("");
  }, [approving, rejecting]);

  const close = () => {
    setNote("");
    onClose();
  };

  const done = () => {
    setNote("");
    onDone();
  };

  const doApprove = async () => {
    if (!approving) return;
    setBusy(true);
    try {
      await approveApplicationRequest(approving.id, note);
      toast.success("Approved — the application entry was created with the partner's details");
      close();
      done();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Could not approve the request");
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    if (!note.trim()) {
      toast.warning("Add a reason so the partner knows what to fix");
      return;
    }
    setBusy(true);
    try {
      await rejectApplicationRequest(rejecting.id, note.trim());
      toast.success("Returned to the partner with your reason");
      close();
      done();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Could not return the request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={Boolean(approving)} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Approve this request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This creates the {MODULE_LABELS[approving?.moduleType]?.toLowerCase() || "application"}{" "}
            entry for {approving?.clientName || "the client"} with{" "}
            {approving?.partnerName || "the partner"}&apos;s details filled in, shared with the
            referral portal. You can complete any missing fields afterwards in the module form.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Note for the record (optional)"
            value={approving ? note : ""}
            onChange={(event) => setNote(event.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={doApprove} variant="contained" color="success" disabled={busy}>
            {busy ? "Approving…" : "Approve & create entry"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(rejecting)} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>Return this request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            <History fontSize="small" /> The partner sees your reason and can edit the details and
            resubmit.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            required
            label="Reason (shown to the partner)"
            value={rejecting ? note : ""}
            onChange={(event) => setNote(event.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={doReject} variant="contained" color="error" disabled={busy}>
            {busy ? "Returning…" : "Return to partner"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ApplicationReviewDialogs;
